import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { authApi } from '../lib/api';
import { Mail, Lock, LogIn } from 'lucide-react';
import { usePermissions } from '../contexts/PermissionsContext';
import { useBrands } from '../contexts/BrandsContext';
import { useTheme } from '../contexts/ThemeContext';

const Login = () => {
  const navigate = useNavigate();
  const { refetchPermissions } = usePermissions();
  const { refetch: refetchBrands } = useBrands();
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authApi.login(formData);
      const { access_token, refresh_token, user } = response.data;
      
      localStorage.setItem('access_token', access_token);
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
      }
      localStorage.setItem('user', JSON.stringify(user));
      
      // AUTO-SET BRAND CONTEXT: Backend now provides full brand objects
      if (user.default_brand_id && user.brands && user.brands.length > 0) {
        const defaultBrand = user.brands.find(b => b.id === user.default_brand_id);
        
        if (defaultBrand) {
          const context = {
            clientId: defaultBrand.client_id,
            clientName: defaultBrand.client_name || 'Client',
            brandId: defaultBrand.id,
            brandName: defaultBrand.name,
            client: { id: defaultBrand.client_id, name: defaultBrand.client_name },
            brand: { id: defaultBrand.id, name: defaultBrand.name }
          };
          
          localStorage.setItem('selectedContext', JSON.stringify(context));
        }
      } else if (user.brands && user.brands.length > 0) {
        // Fallback: auto-select first available brand if no default is set
        const firstBrand = user.brands[0];
        const context = {
          clientId: firstBrand.client_id,
          clientName: firstBrand.client_name || 'Client',
          brandId: firstBrand.id,
          brandName: firstBrand.name,
          client: { id: firstBrand.client_id, name: firstBrand.client_name },
          brand: { id: firstBrand.id, name: firstBrand.name }
        };
        
        localStorage.setItem('selectedContext', JSON.stringify(context));
      }
      
      // Fetch permissions and brands/clients immediately after login (force fresh fetch)
      await Promise.all([
        refetchPermissions(),
        refetchBrands()
      ]);
      
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundImage: theme?.login_background ? `url(${theme.login_background})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: theme?.login_background ? 'transparent' : '#f8fafc'
      }}
    >
      {/* Overlay for better readability when background image is used */}
      {theme?.login_background && (
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
      )}
      
      <div className="w-full max-w-md relative z-10">
        <div className="bg-white border border-slate-200 shadow-xl rounded-lg p-8">
          <div className="mb-8 text-center">
            <div className="flex justify-center mb-6">
              <img 
                src={theme?.logo_url || "/logo-2plus.png"} 
                alt={`${theme?.platform_name || 'Platform'} Logo`} 
                className="h-16 w-auto object-contain" 
              />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight mb-2">
              Welcome Back
            </h1>
            <p className="text-slate-600">Sign in to your account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full text-white"
              style={{ backgroundColor: theme?.primary_color || '#C29A6F' }}
              disabled={loading}
              data-testid="login-button"
              onMouseEnter={(e) => !loading && (e.target.style.opacity = '0.9')}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <LogIn className="h-4 w-4" />
                  Sign In
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Don't have an account?{' '}
              <Link 
                to="/signup" 
                className="hover:underline font-medium"
                style={{ color: theme?.primary_color || '#C29A6F' }}
              >
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
